import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { AreaButton, KOGAreaButtons } from '@/main/components/shared/styles';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';

// Api's
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getStatusRegister,
  useStatusRegisterReducer
} from '@/main/store/statusRegister';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import {
  getSupervisorsDomain,
  useSupervisorReducer,
  getSupervisors
} from '@/main/store/supervisor';

import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';

import { KeyValue } from '@/main/models/common';

import { getDefaultOptionDrop, getTranslateByKey, removeEmptyValues } from '@/main/services/utils';
import { RoutesConstants } from '@/main/routes/routesConstants';
import SupervisorSearchGrid, {
  IGridData,
  INITIAL_GRID_STATE
} from './components/supervisorSearchGrid/supervisorSearchGrid';
import * as S from './styles';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';
import { InsurerFilterRequest } from '@/main/models/request';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { userIsAdmin, userIsInsurer } from '@/main/services/utilsUser';
import { SupervisorFilterRequest } from '@/main/models/request/supervisorRequest';

const INITIAL_FORM_STATE = {
  country: null as KeyValue | null,
  name: null as KeyValue | null,
  rfc: '' as string,
  statusRegister: null as KeyValue | null,
  insurer: null as KeyValue | null
};

type ISearchSupervisorForm = typeof INITIAL_FORM_STATE;

function SupervisorScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const countryReducer = useCountryReducer();
  const statusRegisterReducer = useStatusRegisterReducer();
  const { insurers: insurerReducer } = useInsurerReducer();
  const { domain: supervisorDomain, supervisors } = useSupervisorReducer();
  const [searchParams, setSearchParams] = useSearchParams();

  const optionDefaultCountryDrop = getDefaultOptionDrop(
    countryReducer.countriesUser.data
  );

  const optionDefaultInsurerDrop = userIsInsurer()
    ? getDefaultOptionDrop(insurerReducer.data)
    : null;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<ISearchSupervisorForm>({
    defaultValues: INITIAL_FORM_STATE
  });

  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
  const [formFilters, setFormFilters] = useState<ISearchSupervisorForm | null>(
    null
  );
  const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);

  const isDisabledCountry = () => {
    // Desabilita apenas se o usuário tem acesso a apenas um país
    return countryReducer.countriesUser.data?.length === 1;
  };

  const isDisabledInsurerDrop = () => insurerReducer.data === null || watch('country.key') === null || insurerReducer.data.length === 1;
  const isDisableSearchButton = () => insurerReducer.isLoading || countryReducer.countriesUser.isLoading || supervisorDomain.isLoading;

  function callInsurerApiDomain() {
    // Clean fields & reducers
    dispatch(resetReducerInsurers());

    if (optionDefaultCountryDrop !== null) {
      const request: InsurerFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };

      dispatch(getInsurers(request));
    }
  }

  useEffect(() => {
    // Carrega os países do usuário
    dispatch(getCountriesUser());
    dispatch(getStatusRegister());
  }, []);

  useEffect(() => {
    callInsurerApiDomain();

    // Se o usuário tem acesso a apenas uma seguradora, preenche automaticamente
    if (insurerReducer.data?.length === 1) {
      const defaultInsurer = insurerReducer.data[0];
      setValue('insurer', {
        key: defaultInsurer.key,
        value: defaultInsurer.value
      });
    }
  }, [countryReducer.countriesUser.data]);

  useEffect(() => {
    if (insurerReducer.data && !insurerReducer.isLoading) {
      const isTypeCompanyUserInsurer = userIsInsurer();
      if (isTypeCompanyUserInsurer && optionDefaultInsurerDrop) {
        setValue('insurer', optionDefaultInsurerDrop);
      }
    }
  }, [insurerReducer.data, insurerReducer.isLoading]);

  useEffect(() => {
    const country = watch('country');

    if (country?.key) {
      const request: InsurerFilterRequest = {
        guidCountry: String(country.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };

      dispatch(getInsurers(request));

      dispatch(getSupervisorsDomain({
        guidCountry: String(country.key)
      }));
    }
  }, [watch('country')]);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    if (Object.keys(params).length > 0) {
      const formData = {
        country: params.country
          ? { key: params.country, value: params.countryValue }
          : null,
        name: params.name
          ? { key: params.name, value: params.nameValue }
          : null,
        rfc: params.rfc || '',
        statusRegister: params.statusRegister
          ? { key: params.statusRegister, value: params.statusRegisterValue }
          : null,
        insurer: params.insurer
          ? { key: params.insurer, value: params.insurerValue }
          : null
      } as ISearchSupervisorForm;

      setFormFilters(formData);
      setValue('country', formData.country);
      setValue('name', formData.name);
      setValue('rfc', formData.rfc);
      setValue('statusRegister', formData.statusRegister);
      setValue('insurer', formData.insurer);
    }
  }, [searchParams]);

  function onSubmit(formInputs: ISearchSupervisorForm) {
    setPageIndex(PaginationEnum.PageIndexIntial);
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });
    const requestForm: SupervisorFilterRequest = {
      guidCountry: formInputs.country?.key ? String(formInputs.country.key) : undefined,
      guidSupervisor: formInputs.name?.key ? String(formInputs.name.key) : undefined,
      guidInsurer: formInputs.insurer?.key ? String(formInputs.insurer.key) : undefined,
      registrationNumber: formInputs.rfc,
      name: formInputs.name?.value ? String(formInputs.name.value) : undefined,
      idStatusRegister: formInputs.statusRegister?.key ? Number(formInputs.statusRegister.key) : undefined,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30
    };

    setFormFilters({
      country: formInputs.country,
      name: formInputs.name,
      rfc: formInputs.rfc || '',
      statusRegister: formInputs.statusRegister,
      insurer: formInputs.insurer
    });

    // Atualiza os parâmetros da URL
    const params = new URLSearchParams();
    if (formInputs.country) {
      params.set('country', String(formInputs.country.key));
      params.set('countryValue', formInputs.country.value);
    }
    if (formInputs.name) {
      params.set('name', String(formInputs.name.key));
      params.set('nameValue', formInputs.name.value);
    }
    if (formInputs.rfc) {
      params.set('rfc', formInputs.rfc);
    }
    if (formInputs.statusRegister) {
      params.set('statusRegister', String(formInputs.statusRegister.key));
      params.set('statusRegisterValue', formInputs.statusRegister.value);
    }
    if (formInputs.insurer) {
      params.set('insurer', String(formInputs.insurer.key));
      params.set('insurerValue', formInputs.insurer.value);
    }
    setSearchParams(params);

    const requestFormatted = removeEmptyValues(requestForm);

    dispatch(getSupervisors(requestFormatted));
    setShowFilters(!showFilters);
  }

  useEffect(() => {
    if (supervisors?.data) {
      setGridData({
        isLoading: supervisors.isLoading,
        totalItems: supervisors.data.totalNumberOfRecords,
        data: supervisors.data.results
      });
    }
  }, [supervisors.data]);

  const onReset = () => {
    if (countryReducer.countriesUser.data?.length === 1) {
      setValue('country', getDefaultOptionDrop(countryReducer.countriesUser.data));
    }
    if (insurerReducer.data && insurerReducer.data?.length > 1) {
      setValue('insurer', null);
    }
    setValue('name', null);
    setValue('rfc', '');
    setValue('statusRegister', null);
    setGridData(INITIAL_GRID_STATE)
    setSearchParams({});
    setFormFilters(null);
    setPageIndex(PaginationEnum.PageIndexIntial);
  };

  function handleNew() {
    navigate(RoutesConstants.registrationRegisterSupervisor);
  }

  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  const canCreateNew = userIsAdmin() || userIsInsurer();
  return (
    <ContainerScreen
      hasBackground
      title={`
      ${getTranslateByKey('information.registration')}
      ${' > '}
      ${getTranslateByKey('information.searchSupervisor')}`}>
      <S.Container>
        <AreaButton>
          <Button
            onClick={handleFilters}
            id="id-searchUserGroup-button-filters">
            {getTranslateByKey('information.filters')}
          </Button>
          {canCreateNew && (
            <Button
              onClick={handleNew}
              id="id-searchUserGroup-button-newSupervisor">
              {getTranslateByKey('information.newSupervisor')}
            </Button>
          )}
        </AreaButton>
        <Box
          showBox={showFilters}
          title={getTranslateByKey('information.filters')}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <S.RowInputs>
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.country')}
                control={control}
                nameCustom="country"
                rulesCustom={{ required: true }}
                errorMessageCustom={
                  errors.country?.type === 'required'
                    ? getTranslateByKey('information.countryRequired', true)
                    : ''
                }
                hasErrorCustom={!!errors.country}
                options={countryReducer.countriesUser.data}
                isLoading={countryReducer.countriesUser.isLoading}
                onChangeCustom={() => { }}
                disabled={isDisabledCountry()}
                autoHighlightCustom={
                  countryReducer.countriesUser.data?.length === 1
                }
                setValueCustom={setValue}
                id="id-searchSupervisor-txt-country"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.name')}
                control={control}
                nameCustom="name"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={supervisorDomain.data || []}
                isLoading={supervisorDomain.isLoading}
                onChangeCustom={() => { }}
                id="id-searchSupervisor-txt-name"
              />
              <KogInputTextControlledNew
                label={getTranslateByKey('information.rfc')}
                helperText={
                  errors.rfc?.type === 'minLength' ||
                    errors.rfc?.type === 'pattern'
                    ? getTranslateByKey(
                      'information.rfcMustHaveAtLeast12Digits',
                      true
                    )
                    : ''
                }
                name="rfc"
                error={!!errors.rfc}
                inputProps={{
                  maxLength: 12,
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                controllerProps={{
                  rules: {
                    minLength: 12,
                    pattern: /^\d{12}$/,
                    validate: value => !value || value.length === 12
                  }
                }}
                control={control}
                id="id-searchSupervisor-txt-rfc"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.status')}
                nameCustom="statusRegister"
                valueDefaultKey={String(StatusRegisterEnum.Active)}
                errorMessageCustom=""
                control={control}
                setValueCustom={setValue}
                options={statusRegisterReducer.statusRegister.data}
                isLoading={statusRegisterReducer.statusRegister.isLoading}
                rulesCustom={{ required: false }}
                hasErrorCustom={false}
                onChangeCustom={() => { }}
                id="id-searchSupervisor-txt-statusRegister"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.insurer')}
                control={control}
                nameCustom="insurer"
                hasErrorCustom={!!errors.insurer}
                options={insurerReducer.data}
                isLoading={insurerReducer.isLoading}
                onChangeCustom={() => { }}
                disabled={isDisabledInsurerDrop()}
                isDefaultValueUnique={optionDefaultInsurerDrop !== null}
                setValueCustom={setValue}
                defaultValue={watch('insurer.key')}
                id="id-searchSupervisor-txt-insurer"
              />
            </S.RowInputs>
            <KOGAreaButtons>
              <SecondaryButton
                id="id-searchSupervisor-button-submit"
                type="submit"
                disabled={isDisableSearchButton()}>
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                id="id-searchSupervisor-button-clear"
                onClick={onReset}>
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </KOGAreaButtons>
          </form>
        </Box>

        <SupervisorSearchGrid
          formFilters={formFilters}
          gridData={gridData}
          setGridData={setGridData}
        />
      </S.Container>
    </ContainerScreen>
  );
}

export default withQueryparams(SupervisorScreen);